import { Button, Modal } from "antd";
import React, { useState } from "react";
import { toast } from "react-toastify";
import { useApi } from "src/Api/useApi";
import AuthorizationWrapper from "src/common/AuthorizationWrapper";
import { AdminPermissions } from "src/common/Permissions";

type Props = {
	organizationId: number;
	userId: number;
	/** called after a successful re-sync so the parent can reload what it shows */
	onSynced?: () => void;
};

/**
 * Manual fallback for a missed player event: asks Spotlightr for this one user's progress on every enrolled
 * course and re-evaluates completion. Global admin only - the API enforces the same.
 */
const ResyncTrainingButton: React.FC<Props> = ({
	organizationId,
	userId,
	onSynced,
}) => {
	const { trainingCourseClient } = useApi();
	const [open, setOpen] = useState(false);
	const [busy, setBusy] = useState(false);

	const run = async () => {
		if (!trainingCourseClient) return;
		setBusy(true);
		try {
			const results = await trainingCourseClient.resyncUserTraining(
				organizationId,
				userId
			);
			const complete = results.filter((r) => r.courseComplete).length;
			toast.success(
				`Training re-synced: ${results.length} course(s), ${complete} complete`
			);
			setOpen(false);
			if (onSynced) onSynced();
		} catch (e) {
			toast.error("Failed to re-sync training");
		} finally {
			setBusy(false);
		}
	};

	return (
		<AuthorizationWrapper
			type="hide"
			permissions={[AdminPermissions.GlobalAdmin]}
		>
			<span>
				<Button className="btn-secondary" onClick={() => setOpen(true)}>
					Re-sync training
				</Button>
				<Modal
					visible={open}
					title="Re-sync training from Spotlightr?"
					onCancel={() => setOpen(false)}
					onOk={run}
					okText="Re-sync"
					okButtonProps={{ loading: busy, disabled: busy }}
				>
					<p>
						This asks Spotlightr for this user&apos;s progress on every enrolled
						course and re-evaluates completion. Use it when a learner finished a
						video but the course did not update.
					</p>
				</Modal>
			</span>
		</AuthorizationWrapper>
	);
};

export default ResyncTrainingButton;
